import {type adminInsertT, type adminT, type adminUpdateT, useViewQuery} from '@tnc/shared';
import {useRef, useState} from 'react';
import {Link} from 'react-router';

import {FormFieldT} from '@/lib/types/form.types';
import {statsRowT} from '@/lib/types/stats.types';

import {createDataTableConfig} from '@/lib/helpers/config';

import {useAdmins} from '@/providers/components/adminsProvider';
import {useAuth} from '@/providers/components/authProvider';

import {ConfirmDeleteModalComponent, ModalFormComponent, StatsRowComponent} from '@/components';
import Page from '@/components/Page';
import {DataTable} from '@/components/dataTable';

const initialFormData: adminInsertT = {
  admin_speculator_id: 0,
  created_by: '',
  is_active: false,
  uid: '',
};

const createFields: FormFieldT<adminT>[] = [
  {
    label: 'Username',
    name: 'admin_speculator_id',
    type: 'select',
    selectOptions: [],
    placeholder: '',
    required: false,
  },
];

const editFields: FormFieldT<adminT>[] = [...createFields];

function AdminAdmins() {
  const {isSuperAdmin} = useAuth();
  const {createData, updateData, deleteData} = useAdmins();

  // State
  const [isVisible, setIsVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Refs
  const selectedAdminIdRef = useRef<number | null>(null);
  const formDataRef = useRef<adminInsertT>(initialFormData);

  // Reactivity
  const {data: stats, refetch: refetchStats} = useViewQuery({
    view: 'admin_stats',
  });

  // Functions
  const handleCreateAdmin = async (data: adminInsertT) => {
    try {
      setIsSubmitting(true);
      await createData(data);
      refetchStats();
      setIsVisible(false);
    } catch (err) {
      setSubmissionError(`Failed to create admin ${err}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateAdmin = async (data: adminUpdateT) => {
    if (selectedAdminIdRef.current === null) {
      setSubmissionError('No admin selected');
      return;
    }

    try {
      setIsSubmitting(true);
      await updateData(data);
      refetchStats();
      setIsVisible(false);
    } catch (err) {
      setSubmissionError(`Failed to update admin: ${err}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteAdmin = async () => {
    if (selectedAdminIdRef.current === null) {
      setDeleteError('No speculator selected');
      return;
    }

    try {
      setIsSubmitting(true);
      await deleteData(selectedAdminIdRef.current);
      refetchStats();
      setIsDeleteModalVisible(false);
    } catch (err) {
      setSubmissionError(`Failed to delete user ${err}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddAdminClick = async () => {
    if (!isSuperAdmin) return;
    formDataRef.current = initialFormData;
    setIsEditing(false);
    setIsVisible(true);
  };

  const handleEditAdminClick = (admin: adminT) => {
    formDataRef.current = admin;
    selectedAdminIdRef.current = admin.admin_id;
    setIsEditing(true);
    setIsVisible(true);
  };

  const handleDeleteAdminClick = (admin: adminT) => {
    selectedAdminIdRef.current = admin.admin_id;
    setIsDeleteModalVisible(true);
  };

  // Data Table -----------------------------------------------------
  const dataTableConfig = createDataTableConfig({
    table: 'Admin',
    title: 'Admins Summary',
    searchColumn: 'Speculator.speculator_username',
    columns: [
      'admin_id',
      'is_active',
      'role',
      [
        'Speculator',
        ['speculator_id', 'speculator_username', 'speculator_name_first', 'speculator_name_last', 'speculator_city'],
      ],
    ],
    hideAdd: !isSuperAdmin,
    onAdd: handleAddAdminClick,
    columnsDisplay: [
      {
        header: 'Username',
        field: 'admin_speculator_id',
        render: row => (
          <Link to={`/admin/speculators/${row.Speculator?.speculator_id}`} className="text-primary">
            {row.Speculator?.speculator_username}
          </Link>
        ),
      },
      {
        header: 'Status',
        field: 'is_active',
        render: row => (
          <span className={`badge bg-${row.is_active ? 'success' : 'danger'} text-white`}>
            {row.is_active ? 'Active' : 'Inactive'}
          </span>
        ),
      },
      {
        header: 'Role',
        field: 'role',
        render: row => {
          if (!row.role) return <span className="text-danger">No Role</span>;
          return <span className="text-primary">{row.role}</span>;
        },
      },
      {header: 'First Name', field: 'Speculator.speculator_name_first'},
      {header: 'Last Name', field: 'Speculator.speculator_name_last'},
      {header: 'City', field: 'Speculator.speculator_city'},
    ],
    actionsDisplay: [
      {
        icon: 'ri-pencil-line',
        color: 'info',
        handler: row => handleEditAdminClick(row),
        permission: () => isSuperAdmin,
      },
      {
        icon: 'ri-delete-bin-line',
        color: 'danger',
        handler: row => handleDeleteAdminClick(row),
        permission: () => isSuperAdmin,
      },
    ],
  });

  /**********************************************************
   * Stats
   *********************************************************/
  const statsRow: statsRowT = [
    {
      title: 'Total Admins',
      value: stats?.data?.[0]?.total ?? 0,
      subtitle: 'This Month',
      change: 1.03,
      icon: 'ri-team-line',
      color: 'primary',
      cardClass: 'Users-main-card primary',
    },
    {
      title: 'Active Admins',
      value: stats?.data?.[0]?.active ?? 0,
      subtitle: 'This Month',
      change: 0.36,
      icon: 'ri-user-unfollow-line',
      color: 'secondary',
      cardClass: 'Users-main-card secondary',
    },
    {
      title: 'Inactive Admins',
      value: stats?.data?.[0]?.inactive ?? 0,
      subtitle: 'This Month',
      change: -1.28,
      icon: 'ri-service-line',
      color: 'warning',
      cardClass: 'Users-main-card warning',
    },
  ];

  return (
    <Page currentPage="Admins">
      <StatsRowComponent stats={statsRow} />
      <DataTable {...dataTableConfig} />
      <ModalFormComponent
        isVisible={isVisible}
        fields={isEditing ? editFields : createFields}
        formData={formDataRef.current}
        onSubmit={(data: adminT) => {
          setSubmissionError(null);
          if (isEditing) {
            handleUpdateAdmin(data);
          } else {
            handleCreateAdmin(data);
          }
        }}
        onClose={() => setIsVisible(false)}
        error={submissionError}
        isSubmitting={isSubmitting}
        title={isEditing ? 'Edit Admin' : 'Create New Admin'}
      />
      <ConfirmDeleteModalComponent
        isVisible={isDeleteModalVisible}
        onClose={() => setIsDeleteModalVisible(false)}
        onConfirm={handleDeleteAdmin}
        title="Confirm Deletion"
        description="Are you sure you want to delete this admin? This action cannot be undone."
        isSubmitting={isSubmitting}
        error={deleteError}
      />
    </Page>
  );
}

export default AdminAdmins;
